import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faNewspaper, faArrowRight, faMapMarkerAlt, faSpinner,
  faChevronDown, faTimes, faClock, faExternalLinkAlt, faChartBar,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import newsBot    from "../../assets/images/news-bot.png";
import newsMapImg from "../../assets/images/news-map-img.png";

// ─── Colors ───────────────────────────────────────────────────────────────────
const C = {
  dark: "#1a3328", mid: "#2d5a3d", light: "#e8f5ee",
  border: "#d0e8d8", textDk: "#1a2e22", textGr: "#6c757d",
  blue: "#2563eb", purple: "#7c3aed",
};

// ─── RSS Feed Logic ───────────────────────────────────────────────────────────
const RSS2JSON = "https://api.rss2json.com/v1/api.json?rss_url=";
const RSS_FEEDS = [
  { url: "https://economictimes.indiatimes.com/news/economy/agriculture/rssfeeds/11462317.cms", tag: "Agri",    color: { bg: "#fff3e0", text: "#b76a00" } },
  { url: "https://www.agrifarming.in/feed",                                                       tag: "Farming", color: { bg: "#e8f5ee", text: "#2d7a45" } },
  { url: "https://krishijagran.com/rss/",                                                          tag: "Crops",   color: { bg: "#fce4ec", text: "#c62828" } },
  { url: "https://news.google.com/rss/search?q=%E0%A4%B6%E0%A5%87%E0%A4%A4%E0%A5%80+%E0%A4%AA%E0%A5%80%E0%A4%95+%E0%A4%95%E0%A5%83%E0%A4%B7%E0%A5%80&hl=mr&gl=IN&ceid=IN:mr", tag: "मराठी", color: { bg: "#fff8e1", text: "#e65100" } },
  { url: "https://news.google.com/rss/search?q=%E0%A4%96%E0%A5%87%E0%A4%A4%E0%A5%80+%E0%A4%95%E0%A4%BF%E0%A4%B8%E0%A4%BE%E0%A4%A8+%E0%A4%AB%E0%A4%B8%E0%A4%B2&hl=hi&gl=IN&ceid=IN:hi", tag: "हिंदी", color: { bg: "#f3e5f5", text: "#7b1fa2" } },
];
const FALLBACK_NEWS = [
  { title: "Good Monsoon Boosts Kharif Crop Outlook",             link: "#", pubDate: moment().subtract(2,  "hours").toISOString(), thumbnail: null, tag: "Agri",    color: { bg: "#fff3e0", text: "#b76a00" }, source: "ET Agri" },
  { title: "Govt Announces Support Price for Major Pulses",        link: "#", pubDate: moment().subtract(5,  "hours").toISOString(), thumbnail: null, tag: "Farming", color: { bg: "#e8f5ee", text: "#2d7a45" }, source: "Agri Farming" },
  { title: "Agri Export Crosses New Record in FY 2024-25",         link: "#", pubDate: moment().subtract(24, "hours").toISOString(), thumbnail: null, tag: "Crops",   color: { bg: "#fce4ec", text: "#c62828" }, source: "Krishi Jagran" },
  { title: "New Pest-Resistant Soybean Variety Launched by ICAR",  link: "#", pubDate: moment().subtract(30, "hours").toISOString(), thumbnail: null, tag: "Agri",    color: { bg: "#fff3e0", text: "#b76a00" }, source: "ET Agri" },
  { title: "Maharashtra Releases ₹2,400 Cr Drought Relief",        link: "#", pubDate: moment().subtract(48, "hours").toISOString(), thumbnail: null, tag: "Farming", color: { bg: "#e8f5ee", text: "#2d7a45" }, source: "Agri Farming" },
  { title: "MSP hike announced for Kharif crops — wheat & soybean benefit", link: "#", pubDate: moment().subtract(52, "hours").toISOString(), thumbnail: null, tag: "Agri", color: { bg: "#fff3e0", text: "#b76a00" }, source: "ET Agri" },
];

const cleanTitle = (t = "") =>
  t.replace(/&amp;/g, "&").replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, "<").replace(/&gt;/g, ">").trim();

const extractImg = (html = "") => {
  const matches = [...(html || "").matchAll(/<img[^>]+src=["']?([^"'\s>]+)["']?/gi)];
  for (const m of matches) {
    const src = m[1];
    if (src && !src.startsWith("data:") && !src.includes("pixel") && !src.includes("track") && src.length > 20) return src;
  }
  return null;
};

const fetchOgImage = (url) => {
  if (!url || url === "#") return Promise.resolve(null);
  return fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}&meta=false&video=false&audio=false`)
    .then((r) => r.json()).then((d) => d?.data?.image?.url || null).catch(() => null);
};

// ─── Weather Helpers ──────────────────────────────────────────────────────────
const WMO = {
  0: { icon: "☀️", label: "Clear sky" },      1: { icon: "🌤️", label: "Mainly clear" },
  2: { icon: "⛅",  label: "Partly cloudy" },  3: { icon: "☁️",  label: "Overcast" },
  45: { icon: "🌫️", label: "Fog" },           48: { icon: "🌫️", label: "Icy fog" },
  51: { icon: "🌦️", label: "Light drizzle" }, 53: { icon: "🌦️", label: "Drizzle" },       55: { icon: "🌧️", label: "Heavy drizzle" },
  61: { icon: "🌧️", label: "Light rain" },    63: { icon: "🌧️", label: "Rain" },           65: { icon: "🌧️", label: "Heavy rain" },
  71: { icon: "🌨️", label: "Light snow" },    73: { icon: "🌨️", label: "Snow" },           75: { icon: "❄️",  label: "Heavy snow" },
  80: { icon: "🌦️", label: "Rain showers" },  81: { icon: "🌦️", label: "Heavy showers" }, 82: { icon: "⛈️",  label: "Violent showers" },
  95: { icon: "⛈️", label: "Thunderstorm" },  96: { icon: "⛈️", label: "T-storm + hail" }, 99: { icon: "⛈️", label: "Heavy T-storm" },
};
const wmo     = (code) => WMO[code] || { icon: "🌡️", label: "Unknown" };
const DAYS    = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const WDIRS   = ["N","NE","E","SE","S","SW","W","NW"];
const toDir   = (deg) => WDIRS[Math.round(deg / 45) % 8];

// ─── News Item Dialog ─────────────────────────────────────────────────────────
const NewsDialog = ({ item, onClose }) => (
  <div style={S.overlay} onClick={onClose}>
    <div style={S.dialogBox} onClick={(e) => e.stopPropagation()}>
      <button style={S.dialogClose} onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
      {item.thumbnail && (
        <img src={item.thumbnail} alt="" style={S.dialogImg} onError={(e) => (e.target.style.display = "none")} />
      )}
      <div style={S.dialogBody}>
        <span style={{ ...S.tagBadge, backgroundColor: item.color.bg, color: item.color.text }}>{item.tag}</span>
        <div style={S.dialogTitle}>{item.title}</div>
        <div style={S.dialogMeta}>
          <FontAwesomeIcon icon={faClock} style={{ marginRight: 4 }} />
          {moment(item.pubDate).fromNow()}{item.source ? ` · ${item.source}` : ""}
        </div>
        {item.link && item.link !== "#" && (
          <a href={item.link} target="_blank" rel="noopener noreferrer" style={S.dialogReadBtn}>
            Read Full Article <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginLeft: 6 }} />
          </a>
        )}
      </div>
    </div>
  </div>
);

// ─── All News Dialog ──────────────────────────────────────────────────────────
const AllNewsDialog = ({ news, onClose, onSelect }) => (
  <div style={S.overlay} onClick={onClose}>
    <div style={{ ...S.dialogBox, maxWidth: 680, maxHeight: "82vh", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <span style={{ fontWeight: 700, fontSize: "1rem", color: C.textDk }}>📰 All Agriculture News</span>
        <button style={S.dialogClose} onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
      </div>
      <div style={{ overflowY: "auto", flex: 1 }}>
        {news.map((item, i) => (
          <div key={i} style={{ ...S.newsItem, margin: "0 0.75rem", borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 6, marginTop: i === 0 ? 10 : 0 }}
            onClick={() => { onClose(); setTimeout(() => onSelect(item), 80); }}>
            {item.thumbnail
              ? <img src={item.thumbnail} alt="" style={S.newsThumb} onError={(e) => (e.target.style.display = "none")} />
              : <div style={S.newsThumbPh}>📰</div>}
            <div style={S.newsContent}>
              <div style={S.newsTitle}>{item.title}</div>
              <div style={S.newsTime}><FontAwesomeIcon icon={faClock} style={{ marginRight: 3, fontSize: "0.6rem" }} />{moment(item.pubDate).fromNow()} · {item.source}</div>
            </div>
          </div>
        ))}
        <div style={{ height: 10 }} />
      </div>
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const CommodityNews = () => {
  const [news,        setNews]        = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [activeNews,  setActiveNews]  = useState(null);
  const [showAll,     setShowAll]     = useState(false);

  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const defaultLoc = storedUser.district || storedUser.state || "Pune";
  const [inputVal,     setInputVal]     = useState(defaultLoc);
  const [showLocInput, setShowLocInput] = useState(false);
  const [suggestions,  setSuggestions]  = useState([]);
  const [location,     setLocation]     = useState(null);
  const [weather,      setWeather]      = useState(null);
  const [wLoading,     setWLoading]     = useState(false);
  const [wError,       setWError]       = useState("");

  // ── Fetch News ──────────────────────────────────────────────────────────────
  const fetchFeeds = async () => {
    setNewsLoading(true);
    try {
      const results = await Promise.allSettled(
        RSS_FEEDS.map(({ url, tag, color }) =>
          fetch(`${RSS2JSON}${encodeURIComponent(url)}&num=5`)
            .then((r) => r.json())
            .then((data) => {
              if (data?.status !== "ok") return [];
              const source = data?.feed?.title || tag;
              return (data.items || []).map((item) => {
                const thumb =
                  (item.thumbnail && !item.thumbnail.startsWith("data:") ? item.thumbnail : null) ||
                  (item.enclosure?.type?.startsWith("image") ? item.enclosure.link : null) ||
                  extractImg(item.content) || extractImg(item.description) || null;
                return { title: cleanTitle(item.title), link: item.link || "#", pubDate: item.pubDate || new Date().toISOString(), thumbnail: thumb, tag, color, source };
              });
            }),
        ),
      );
      const merged = results
        .filter((r) => r.status === "fulfilled").flatMap((r) => r.value)
        .filter((item) => item.title)
        .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
      const top10 = merged.slice(0, 10);
      const withImg = await Promise.all(top10.map(async (item) => {
        if (item.thumbnail) return item;
        const og = await fetchOgImage(item.link);
        return { ...item, thumbnail: og };
      }));
      setNews(withImg.length ? withImg : FALLBACK_NEWS);
    } catch {
      setNews(FALLBACK_NEWS);
    } finally {
      setNewsLoading(false);
    }
  };

  // ── Fetch Weather ───────────────────────────────────────────────────────────
  const fetchWeather = async (name) => {
    if (!name) return;
    setWLoading(true); setWError(""); setSuggestions([]);
    try {
      const geoRes  = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=1&language=en&format=json`);
      const geoData = await geoRes.json();
      const loc     = geoData?.results?.[0];
      if (!loc) { setWError("Location not found."); setWLoading(false); return; }
      setLocation({ name: loc.name, admin1: loc.admin1 || "" });
      const wRes  = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${loc.latitude}&longitude=${loc.longitude}` +
        `&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,weather_code,wind_speed_10m,wind_direction_10m` +
        `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max` +
        `&timezone=auto&forecast_days=7`
      );
      setWeather(await wRes.json());
    } catch { setWError("Failed to fetch weather."); }
    finally { setWLoading(false); }
  };

  const fetchSuggestions = async (val) => {
    if (val.length < 2) { setSuggestions([]); return; }
    try {
      const res  = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(val)}&count=5&language=en&format=json`);
      const data = await res.json();
      setSuggestions(data?.results || []);
    } catch { setSuggestions([]); }
  };

  useEffect(() => {
    fetchFeeds();
    fetchWeather(defaultLoc);
    const t = setInterval(fetchFeeds, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const cur   = weather?.current;
  const daily = weather?.daily;
  const locLabel = location ? `${location.name}${location.admin1 ? `, ${location.admin1}` : ""}` : inputVal;

  return (
    <div style={S.page}>

      {/* ── Page Header ── */}
      <div style={S.pageHeader}>
        <FontAwesomeIcon icon={faNewspaper} style={{ marginRight: 10, color: "#a8d5b5" }} />
        Commodity News &amp; Market Intelligence
      </div>

      {/* ── Three Cards ── */}
      <div style={S.threeGrid}>

        {/* Card 1: Agriculture AI */}
        <div style={{ ...S.card1, backgroundImage: `url(${newsBot})`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }}>
          <div style={S.cardTopRow}>
            <div style={S.iconGreen}><span style={{ fontSize: "1.4rem" }}>🌱</span></div>
            <div>
              <div style={S.card1Title}>Agriculture AI</div>
              <div style={S.card1Desc}>Get AI-powered insights for crops, diseases, weather, soil &amp; more.</div>
            </div>
          </div>
          <a href="https://vistaar.maharashtra.gov.in/" target="_blank" rel="noopener noreferrer" style={S.btnGreen}>
            Go to Agriculture AI <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 7 }} />
          </a>
        </div>

        {/* Card 2: Mandi Price */}
        <div style={{ ...S.card2, backgroundImage: `url(${newsMapImg})`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }}>
          <div style={S.cardTopRow}>
            <div style={S.iconBlue}><FontAwesomeIcon icon={faChartBar} style={{ color: C.blue, fontSize: "1.2rem" }} /></div>
            <div>
              <div style={S.card2Title}>Search Mandi-wise Commodity Price</div>
              <div style={S.card2Desc}>Check latest commodity prices by mandi across India.</div>
            </div>
          </div>
          <a href="https://www.commodityonline.com/mandiprices" target="_blank" rel="noopener noreferrer" style={S.btnBlue}>
            Search Prices <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 7 }} />
          </a>
        </div>

        {/* Card 3: General News */}
        <div style={S.card3}>
          <div style={S.cardTopRow}>
            <div style={S.iconPurple}><FontAwesomeIcon icon={faNewspaper} style={{ color: C.purple, fontSize: "1rem" }} /></div>
            <div>
              <div style={S.card3Title}>General News</div>
              <div style={S.card3Desc}>Read latest news on agriculture, markets, policies and more.</div>
            </div>
          </div>
          <div style={S.newsList}>
            {newsLoading ? (
              <div style={{ padding: "1.5rem", textAlign: "center", color: C.textGr }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />Loading news…
              </div>
            ) : (
              news.slice(0, 5).map((item, i) => (
                <div key={i} style={S.newsItem} onClick={() => setActiveNews(item)}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f8f8")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
                  {item.thumbnail
                    ? <img src={item.thumbnail} alt="" style={S.newsThumb} onError={(e) => (e.target.style.display = "none")} />
                    : <div style={S.newsThumbPh}>📰</div>}
                  <div style={S.newsContent}>
                    <div style={S.newsTitle}>{item.title}</div>
                    <div style={S.newsTime}><FontAwesomeIcon icon={faClock} style={{ marginRight: 3, fontSize: "0.6rem" }} />{moment(item.pubDate).fromNow()}</div>
                  </div>
                </div>
              ))
            )}
          </div>
          <button style={S.btnPurple} onClick={() => setShowAll(true)}>
            View All News <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 7 }} />
          </button>
        </div>
      </div>

      {/* ── Weather Forecasting ── */}
      <div style={S.weatherCard}>
        {/* Header row */}
        <div style={S.wHeaderRow}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ fontSize: "3rem" }}>⛅</div>
            <div>
              <div style={S.wTitle}>Weather Forecasting</div>
              <div style={S.wSubtitle}>Stay ahead with accurate weather updates for your area.</div>
            </div>
          </div>
          {/* Location picker */}
          <div style={{ position: "relative" }}>
            <div style={S.locPicker} onClick={() => setShowLocInput((v) => !v)}>
              <div style={{ fontSize: "0.68rem", color: C.textGr, marginBottom: 2 }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 4 }} />Your Location
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: "0.9rem", color: C.textDk }}>{locLabel}</span>
                <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: "0.68rem", color: C.textGr }} />
              </div>
            </div>
            {showLocInput && (
              <div style={S.locDropdown}>
                <input
                  autoFocus value={inputVal}
                  onChange={(e) => { setInputVal(e.target.value); fetchSuggestions(e.target.value); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { fetchWeather(inputVal); setShowLocInput(false); } }}
                  placeholder="Search city / district…"
                  style={S.locInput}
                />
                {suggestions.map((s, i) => (
                  <div key={i} style={S.suggItem}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = C.light)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                    onClick={() => {
                      const label = [s.name, s.admin1].filter(Boolean).join(", ");
                      setInputVal(label); setSuggestions([]); setShowLocInput(false); fetchWeather(s.name);
                    }}>
                    📍 {s.name}{s.admin1 ? `, ${s.admin1}` : ""}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Loading / error */}
        {wLoading && !weather && (
          <div style={{ padding: "3rem", textAlign: "center", color: C.textGr }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.8rem", display: "block", margin: "0 auto 10px" }} />
            Fetching weather data…
          </div>
        )}
        {wError && <div style={{ color: "#c62828", padding: "1rem 0" }}>{wError}</div>}

        {/* Weather body */}
        {cur && daily && (
          <div style={S.wBody}>
            {/* Current conditions */}
            <div style={S.wLeft}>
              <div style={{ fontSize: "4.5rem", lineHeight: 1 }}>{wmo(cur.weather_code).icon}</div>
              <div style={S.curTemp}>{Math.round(cur.temperature_2m)}°C</div>
              <div style={S.curCond}>{wmo(cur.weather_code).label}</div>
              <div style={S.statsRow}>
                <div style={S.stat}><div style={S.statLbl}>Humidity</div><div style={S.statVal}>{cur.relative_humidity_2m}%</div></div>
                <div style={S.stat}><div style={S.statLbl}>Wind</div><div style={S.statVal}>{Math.round(cur.wind_speed_10m)} km/h</div></div>
                <div style={S.stat}>
                  <div style={S.statLbl}>Rain Chance</div>
                  <div style={S.statVal}>{daily.precipitation_probability_max ? `${daily.precipitation_probability_max[0]}%` : `${cur.precipitation} mm`}</div>
                </div>
              </div>
            </div>

            {/* 7-day forecast */}
            <div style={S.forecastGrid}>
              {daily.time.slice(0, 7).map((date, i) => {
                const { icon: dIcon } = wmo(daily.weather_code[i]);
                const d    = new Date(date);
                const rain = daily.precipitation_probability_max?.[i] ?? (daily.precipitation_sum[i] > 0 ? Math.round(daily.precipitation_sum[i] * 10) : 0);
                return (
                  <div key={i} style={{ ...S.fDay, ...(i === 0 ? S.fDayToday : {}) }}>
                    <div style={S.fDayName}>{i === 0 ? "Today" : DAYS[d.getDay()]}</div>
                    <div style={S.fDate}>{d.getDate()} {d.toLocaleString("en", { month: "short" })}</div>
                    <div style={{ fontSize: "1.8rem", margin: "4px 0" }}>{dIcon}</div>
                    <div style={S.fHigh}>{Math.round(daily.temperature_2m_max[i])}°C</div>
                    <div style={S.fLow}>{Math.round(daily.temperature_2m_min[i])}°C</div>
                    <div style={S.fRain}>💧 {rain}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={S.wFooter}>
          <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer" style={S.forecastLink}>
            View Detailed Forecast <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 6 }} />
          </a>
        </div>
      </div>

      {/* ── Dialogs ── */}
      {activeNews && <NewsDialog item={activeNews} onClose={() => setActiveNews(null)} />}
      {showAll    && <AllNewsDialog news={news} onClose={() => setShowAll(false)} onSelect={setActiveNews} />}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page:       { padding: "1.5rem 2rem 3rem", backgroundColor: "#f0faf2", minHeight: "calc(100vh - 80px)" },
  pageHeader: { display: "flex", alignItems: "center", padding: "14px 20px", backgroundColor: "#29553D", borderRadius: 10, color: "#fff", fontSize: "1.1rem", fontWeight: 700, marginBottom: 24 },
  threeGrid:  { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 24 },

  // Shared card top row
  cardTopRow: { display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 14 },
  iconGreen:  { width: 50, height: 50, borderRadius: 12, backgroundColor: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 2px 8px rgba(45,90,61,0.18)" },
  iconBlue:   { width: 50, height: 50, borderRadius: 12, backgroundColor: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: "1px solid #bfdbfe" },
  iconPurple: { width: 50, height: 50, borderRadius: 12, backgroundColor: "#f5f3ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: "1px solid #ddd6fe" },

  // Card 1
  card1:      { background: "linear-gradient(135deg, #e8f5ee 0%, #c3ddd0 100%)", borderRadius: 16, padding: "24px 20px 20px", display: "flex", flexDirection: "column", border: "1px solid #b0d9c0", minHeight: 360, overflow: "hidden" },
  card1Title: { fontSize: "1.12rem", fontWeight: 700, color: C.dark, marginBottom: 3 },
  card1Desc:  { fontSize: "0.79rem", color: C.mid, lineHeight: 1.5, margin: 0 },
  btnGreen:   { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "10px 22px", backgroundColor: C.dark, color: "#fff", borderRadius: 25, fontSize: "0.85rem", fontWeight: 600, textDecoration: "none", marginTop: "auto", whiteSpace: "nowrap" },

  // Card 2
  card2:      { background: "#ffffff", borderRadius: 16, padding: "24px 20px 20px", display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", minHeight: 360, overflow: "hidden" },
  card2Title: { fontSize: "1.02rem", fontWeight: 700, color: C.dark, marginBottom: 3, lineHeight: 1.3 },
  card2Desc:  { fontSize: "0.79rem", color: C.textGr, lineHeight: 1.5, margin: 0 },
  btnBlue:    { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "10px 22px", backgroundColor: C.blue, color: "#fff", borderRadius: 25, fontSize: "0.85rem", fontWeight: 600, textDecoration: "none", marginTop: "auto", whiteSpace: "nowrap" },

  // Card 3
  card3:      { background: "#ffffff", borderRadius: 16, padding: "20px", display: "flex", flexDirection: "column", border: "1px solid #e0e0e0", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", minHeight: 300 },
  card3Title: { fontSize: "1.02rem", fontWeight: 700, color: C.dark, marginBottom: 2 },
  card3Desc:  { fontSize: "0.73rem", color: C.textGr, lineHeight: 1.4 },
  newsList:   { flex: 1, display: "flex", flexDirection: "column", gap: 2, marginBottom: 14 },
  newsItem:   { display: "flex", alignItems: "center", gap: 10, padding: "7px 5px", borderRadius: 7, cursor: "pointer", borderBottom: "1px solid #f3f3f3" },
  newsThumb:  { width: 60, height: 48, objectFit: "cover", borderRadius: 7, flexShrink: 0 },
  newsThumbPh:{ width: 60, height: 48, borderRadius: 7, flexShrink: 0, backgroundColor: "#f5f3ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem" },
  newsContent:{ flex: 1, minWidth: 0 },
  newsTitle:  { fontSize: "0.76rem", fontWeight: 600, color: C.textDk, lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", marginBottom: 3 },
  newsTime:   { fontSize: "0.63rem", color: C.textGr, display: "flex", alignItems: "center" },
  btnPurple:  { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "10px 22px", backgroundColor: C.purple, color: "#fff", borderRadius: 25, fontSize: "0.85rem", fontWeight: 600, border: "none", cursor: "pointer", width: "100%", marginTop: "auto" },

  // Weather
  weatherCard: { background: "#ffffff", borderRadius: 16, border: "1px solid #e0e0e0", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", padding: "24px" },
  wHeaderRow:  { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 },
  wTitle:      { fontSize: "1.3rem", fontWeight: 700, color: C.dark, marginBottom: 3 },
  wSubtitle:   { fontSize: "0.8rem", color: C.textGr },
  locPicker:   { padding: "10px 14px", borderRadius: 10, border: `1px solid ${C.border}`, backgroundColor: "#f8fdf9", cursor: "pointer", minWidth: 210, userSelect: "none" },
  locDropdown: { position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 50, width: 290, backgroundColor: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", overflow: "hidden" },
  locInput:    { width: "100%", padding: "10px 14px", border: "none", borderBottom: `1px solid ${C.border}`, fontSize: "0.85rem", outline: "none", boxSizing: "border-box" },
  suggItem:    { padding: "9px 14px", fontSize: "0.8rem", cursor: "pointer", borderBottom: `1px solid #f0f0f0`, color: C.textDk, backgroundColor: "#fff" },

  wBody:      { display: "flex", gap: 24, alignItems: "flex-start" },
  wLeft:      { display: "flex", flexDirection: "column", alignItems: "center", minWidth: 175, paddingRight: 24, borderRight: `1px solid ${C.border}` },
  curTemp:    { fontSize: "3.2rem", fontWeight: 800, color: C.dark, lineHeight: 1, marginTop: 6 },
  curCond:    { fontSize: "0.88rem", color: C.textGr, marginBottom: 16, marginTop: 4 },
  statsRow:   { display: "flex", gap: 18, flexWrap: "wrap", justifyContent: "center" },
  stat:       { textAlign: "center" },
  statLbl:    { fontSize: "0.62rem", color: C.textGr, marginBottom: 2 },
  statVal:    { fontSize: "0.85rem", fontWeight: 700, color: C.dark },

  forecastGrid: { flex: 1, display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 },
  fDay:        { textAlign: "center", padding: "10px 4px", borderRadius: 10, border: "1px solid #e8e8e8", backgroundColor: "#f9fdf9" },
  fDayToday:   { backgroundColor: "#e8f5ee", border: `1px solid ${C.mid}` },
  fDayName:    { fontSize: "0.7rem", fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: "0.03em" },
  fDate:       { fontSize: "0.58rem", color: C.textGr, marginBottom: 2 },
  fHigh:       { fontSize: "0.85rem", fontWeight: 700, color: C.dark },
  fLow:        { fontSize: "0.7rem", color: C.textGr, marginBottom: 2 },
  fRain:       { fontSize: "0.62rem", color: "#1e6fc4" },

  wFooter:      { textAlign: "center", paddingTop: 18, marginTop: 20, borderTop: `1px solid ${C.border}` },
  forecastLink: { color: C.blue, fontSize: "0.9rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center" },

  // Dialogs
  overlay:      { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  dialogBox:    { backgroundColor: "#fff", borderRadius: 16, width: "100%", maxWidth: 520, boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden", position: "relative" },
  dialogClose:  { position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,0.1)", border: "none", borderRadius: "50%", width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", color: "#555", zIndex: 5 },
  dialogImg:    { width: "100%", height: 200, objectFit: "cover", display: "block" },
  dialogBody:   { padding: "18px 22px 22px" },
  tagBadge:     { display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: "0.7rem", fontWeight: 700, marginBottom: 10 },
  dialogTitle:  { fontSize: "1rem", fontWeight: 700, color: C.textDk, lineHeight: 1.4, marginBottom: 8 },
  dialogMeta:   { fontSize: "0.75rem", color: C.textGr, marginBottom: 16, display: "flex", alignItems: "center" },
  dialogReadBtn:{ display: "inline-flex", alignItems: "center", padding: "9px 20px", backgroundColor: C.mid, color: "#fff", borderRadius: 20, fontSize: "0.85rem", fontWeight: 600, textDecoration: "none" },
};

export default CommodityNews;
